// **繋がらないとき・断られたときに、利用者が読める案内を出す**（M6）。
//
// **研究側から受け取ったのは「言い分けの軸」だけで、文言は本番で書いた**
// （現物に「ここの文言はすべて研究用」と明記されている。DESIGN-0006 §3-4）。
// 軸は 2 つ: ①**利用者にできることが違うものを混ぜない**（とくに `timeout` = 回線を疑う と
// `cert` = 回線は無実 を取り違えない）②**画面の文言と到達確認が同じ判定を使う**
// （`reachability.classifyStage`。ADR-0014 §1-2）ので、利用者の訴えと記録が食い違わない。
//
// **文言の正本は `client/画面仕様.md` §7-2。** 変えるときはあちらも直す。
// **補足の言い添えを付けない**（2026-10-21 のユーザー指示。同 §6-3 の囲み）。

import type { ErrorCode } from "./control.ts";
import type { Stage } from "./reachability.ts";

/**
 * **WebTransport が使えるブラウザ**（対応ブラウザの明示 = M7 に使う）。
 *
 * **1 か所だけに置く**（2 か所に書くと必ず片方が古くなる）。出す場所は画面仕様 §9 の未決 5。
 */
export const SUPPORTED_BROWSERS = "Chrome 97 以降 / Edge 98 以降 / Firefox 114 以降 / Safari 26.4 以降";

/** 画面に出す案内。**組み方は画面側が決める**（この file は文言だけを持つ）。 */
export interface Notice {
  /** 何が起きたか。**1 文・句点なし**。 */
  head: string;
  /** どうすればよいか。**打つ手が無いときは省く**。 */
  action?: string;
  /** **問い合わせ用に版と時刻を添えるか**（原因を見分けられなかったときだけ）。 */
  detail?: boolean;
}

/**
 * 接続に失敗した理由を案内にする。
 *
 * 段は到達確認が付けたもの（`reachability.ts` の 5 値）をそのまま使う。
 * **研究側の実装にある `url` の段は本番に無い**（宛先を手で入れる画面ではなく、招待 URL から入る）。
 */
export function connectionNotice(stage: Stage): Notice {
  switch (stage) {
    case "timeout":
      // 返事が来ない = UDP が捨てられている疑いが濃い。**回線を変えるのが唯一の手**
      // （フォールバックは本番に未実装。ADR-0018）
      return { head: "サーバから返事がありません", action: "別の回線で試してください" };
    case "cert":
      // **UDP は通っている**（握手の返事は来た）。回線を疑わせない
      return { head: "証明書で断られました", action: "接続先を確かめてください" };
    case "refused":
      return { head: "接続を拒否されました", action: "しばらく置いてからもう一度試してください" };
    case "unsupported":
      return { head: "このブラウザでは通話を使えません", action: `${SUPPORTED_BROWSERS} でお試しください` };
    case "other":
      return { head: "繋がりませんでした", action: "別の回線で試してください", detail: true };
  }
}

/**
 * サーバに断られた理由を案内にする。**種別はプロトコル側の定義をそのまま使う**
 * （書き写すと増えたときに取りこぼす）。
 *
 * サーバが付ける `message` は開発者向けなので利用者には見せない（ログには残る）。
 */
export function rejectionNotice(code: ErrorCode): Notice {
  switch (code) {
    case "room_full":
      // **満員と上限は 2026-10-21 に別の種別に分けた**（画面仕様 §9 の未決 2 の決着）。
      // ここは「このチャンネルが定員いっぱい」だけを意味する
      return { head: "満員です" };
    case "too_many_groups":
      // **打つ手が無いので `action` を付けない**（運用者が `groups.conf` の行を消すしかない）
      return { head: "グループの上限に達しました" };
    case "too_many_channels":
      return { head: "チャンネルの上限に達しました" };
    case "unsupported_version":
      // ブラウザは配り直しが効くので軽い
      return { head: "読み込み直してください" };
    case "invalid_request":
      // 無いグループ・畳まれたチャンネル・不正な表示名がここに来る。
      // **手順違反も同じ種別**だが利用者には見分けられないので、起こりやすいほうに寄せる
      return { head: "その招待 URL は使えません" };
    case "internal":
      return { head: "接続できませんでした", action: "しばらく置いてからもう一度試してください", detail: true };
  }
}

/**
 * **問い合わせ用の 1 行**（`detail` が立った案内に添える）。
 *
 * **画面には「version」と出す**（2026-10-21 のユーザー指定。「版」とは書かない。画面仕様 §6-3）。
 *
 * 時刻は端末の時計で、分までにする（秒まで出しても問い合わせの役に立たない）。
 */
export function detailLine(appVersion: string, at: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  const when = `${at.getFullYear()}-${p(at.getMonth() + 1)}-${p(at.getDate())} ${p(at.getHours())}:${p(at.getMinutes())}`;
  return `version ${appVersion} / ${when}`;
}
